import { useQuery } from "@apollo/client";
import { useState, useEffect, useMemo } from "react";
import { ACCOUNTS } from "../graphql/account";
import AccountType from "../enums/AccountType";
import AccountStatus from "../enums/AccountStatus";

export interface ExternalAccount {
  id: string;
  name: string;
  url: string;
  status: AccountStatus;
  accountType: string;
  /**
   * Epoch millis of the last successful sync, stamped by the Plaid transaction
   * sync. Null until an account has synced at least once.
   */
  refreshedAt?: number | null;
}

interface UseGetAccountsOptions {
  accountTypes?: string[];
  statuses?: AccountStatus[];
  skip?: boolean;
  onCompleted?: (data: {
    accounts: ExternalAccount[];
    hasDisconnectedAccount: boolean;
  }) => void;
  onError?: (error: any) => void;
}

const useGetAccounts = (options?: UseGetAccountsOptions) => {
  const [counter, setCounter] = useState(0);
  const {
    accountTypes = [AccountType.PLAID_EXTERNAL_CARD_ACCOUNT],
    statuses = [AccountStatus.ACTIVE, AccountStatus.DISCONNECTED],
    skip = false,
    onCompleted,
    onError,
  } = options || {};

  const { data, loading, error, refetch } = useQuery(ACCOUNTS, {
    fetchPolicy: "cache-and-network",
    variables: {
      accountType: accountTypes,
      status: statuses,
    },
    skip,
  });

  const externalAccounts = useMemo(() => {
    return (data?.accounts?.edges || []).map((edge: any) => edge.node);
  }, [data]);

  const getAccountsByStatus = (status: AccountStatus) => {
    return externalAccounts.filter(
      (account: ExternalAccount) => account.status === status,
    );
  };

  const activeAccounts = useMemo(
    () => getAccountsByStatus(AccountStatus.ACTIVE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [externalAccounts],
  );

  const disconnectedAccounts = useMemo(
    () => getAccountsByStatus(AccountStatus.DISCONNECTED),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [externalAccounts],
  );

  const hasDisconnectedAccount = disconnectedAccounts.length > 0;

  useEffect(() => {
    if (!loading && data && onCompleted) {
      onCompleted({
        accounts: externalAccounts,
        hasDisconnectedAccount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, externalAccounts, hasDisconnectedAccount, counter]);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRefetch = async () => {
    await refetch();
    setCounter((prev) => prev + 1);
  };

  return {
    externalAccounts,
    loading,
    error,
    activeAccounts,
    disconnectedAccounts,
    refetch: handleRefetch,
    getAccountsByStatus,
  };
};

export default useGetAccounts;
